import type { EditHistoryChange } from '@/Network/Types/Responses/EditHistoryResponses'
import { COVERAGE_ACTION_LABELS } from '@/utils/coverage'
import type { CoverageActionLabel } from '@/utils/coverage'

export type CoverageHistoryKind = 'UNIDENTIFIED' | 'IDENTIFIED' | 'RELEASED' | 'RESPONSIBLE_CHANGED'

export interface CoverageHistoryEvent {
  kind: CoverageHistoryKind
  /** Rótulo em pt-BR (COVERAGE_ACTION_LABELS), nunca o código técnico. */
  badge: CoverageActionLabel
  title: string
  statusFrom: string | null
  statusTo: string | null
}

const UNREGISTERED_KEYS = ['unregistered_doctor', 'unregisteredDoctor']
const RESPONSIBLE_ID_KEYS = ['coverage_responsible_id', 'coverageResponsibleId']
const RESPONSIBLE_KEYS = [
  'coverage_responsible',
  'coverageResponsible',
  'coverage_responsible_name',
  'coverageResponsibleName',
  ...RESPONSIBLE_ID_KEYS
]
const DOCTOR_KEYS = ['doctor', 'doctor_id', 'doctorId']

const OPEN = 'Vaga aberta'
const DOCTOR = 'Com médico'
const UNIDENTIFIED = 'Ocupado s/ ident.'

type Value = EditHistoryChange['from']

const isEmpty = (value: Value) => value === null || value === '' || value === 'Vazio'
const isTrue = (value: Value) => value === true || value === 'true' || value === 'Sim'
const text = (value: Value) => (isEmpty(value) ? null : String(value))

const find = (changes: EditHistoryChange[], keys: string[]) =>
  changes.find((change) => keys.includes(change.key))

// O formato do diff do responsável no audit ainda não está fechado no backend:
// quando vier só o id (chave `*_id` ou valor numérico), não há nome legível
// para exibir — o título cai no texto genérico em vez de imprimir o número.
const responsibleName = (change: EditHistoryChange | undefined, value: Value): string | null => {
  if (!change || RESPONSIBLE_ID_KEYS.includes(change.key)) return null
  const raw = value as unknown
  if (typeof raw === 'number') return null
  const name = text(value)
  if (!name || /^\d+$/.test(name.trim())) return null
  return name
}

const unidentifiedEvent = (
  responsible: EditHistoryChange | undefined,
  doctor: EditHistoryChange | undefined
): CoverageHistoryEvent => {
  const name = responsibleName(responsible, responsible?.to ?? null)
  const suffix = name ? ` (responsável: ${name})` : ''
  return {
    kind: 'UNIDENTIFIED',
    badge: COVERAGE_ACTION_LABELS.MARK_UNIDENTIFIED,
    title: `Marcado como ocupado sem identificação${suffix}`,
    statusFrom: doctor && !isEmpty(doctor.from) ? DOCTOR : OPEN,
    statusTo: UNIDENTIFIED
  }
}

const leftUnidentifiedEvent = (doctor: EditHistoryChange | undefined): CoverageHistoryEvent => {
  const identified = doctor ? text(doctor.to) : null
  if (identified) {
    return {
      kind: 'IDENTIFIED',
      badge: COVERAGE_ACTION_LABELS.IDENTIFY_DOCTOR,
      title: `Médico identificado: ${identified}`,
      statusFrom: UNIDENTIFIED,
      statusTo: DOCTOR
    }
  }
  return {
    kind: 'RELEASED',
    badge: COVERAGE_ACTION_LABELS.RELEASE_OPEN,
    title: 'Vaga liberada (era ocupado sem identificação)',
    statusFrom: UNIDENTIFIED,
    statusTo: OPEN
  }
}

const responsibleChangedEvent = (responsible: EditHistoryChange): CoverageHistoryEvent => {
  const fromName = responsibleName(responsible, responsible.from)
  const toName = responsibleName(responsible, responsible.to)
  return {
    kind: 'RESPONSIBLE_CHANGED',
    badge: COVERAGE_ACTION_LABELS.CHANGE_RESPONSIBLE,
    title:
      fromName && toName
        ? `Responsável pela cobertura alterado: ${fromName} → ${toName}`
        : 'Responsável pela cobertura alterado',
    statusFrom: null,
    statusTo: null
  }
}

// Sem a flag no diff (ex.: criação), o `type` do registro decide.
const becameUnidentified = (flag: EditHistoryChange | undefined, type?: string | null) =>
  flag ? !isTrue(flag.from) && isTrue(flag.to) : type === 'UNIDENTIFIED'

const leftUnidentified = (flag: EditHistoryChange | undefined) =>
  !!flag && isTrue(flag.from) && !isTrue(flag.to)

/**
 * M12-A — lê o diff do audit e devolve a frase do evento de cobertura, ou
 * `null` para edições comuns (o HistoryCard segue com o layout de hoje).
 */
export function describeCoverageHistoryEvent(
  changes: EditHistoryChange[] | null | undefined,
  type?: string | null
): CoverageHistoryEvent | null {
  const list = changes ?? []
  const flag = find(list, UNREGISTERED_KEYS)
  const responsible = find(list, RESPONSIBLE_KEYS)
  const doctor = find(list, DOCTOR_KEYS)

  if (becameUnidentified(flag, type)) return unidentifiedEvent(responsible, doctor)
  if (leftUnidentified(flag)) return leftUnidentifiedEvent(doctor)
  if (responsible && !isEmpty(responsible.from) && !isEmpty(responsible.to)) {
    return responsibleChangedEvent(responsible)
  }
  return null
}
